import { Length } from "@shared/common/utils/length";
import { z } from "zod/v3";
import { DirectionalList } from "./models/types/directionalList";
import { Point } from "@opstel/geo/Point";

// NOTE:
// Helpers to provide intellisense when defining a schema for already known objects.
// Especially useful if validating & parsing proto schemas
// Original inspiration by: https://github.com/colinhacks/zod/issues/372#issuecomment-2027574367

type SameOutputAndInputShape<Output> = {
  [K in keyof Output]: z.ZodType<Output[K]>;
};

type AnotherOutputAndInputShape<Output, Input> = {
  [K in keyof Input & keyof Output]: z.ZodType<Output[K], z.ZodTypeDef, Input[K]>;
} & {
  [K in keyof Input as K extends keyof Output ? never : K]: z.ZodType<
    Input[K],
    z.ZodTypeDef,
    Input[K]
  >;
};

export function createSchema<Output, Input = null>(
  shape: Input extends null
    ? Required<SameOutputAndInputShape<Output>>
    : Required<AnotherOutputAndInputShape<Output, Input>>,
  params?: z.RawCreateParams,
) {
  return z.object(shape, params);
}

export type ZodOutputFor<T> = z.ZodType<T, z.ZodTypeDef, unknown>;

type ObjectWithLengthInMm = { lengthInMm: number } & Record<string, unknown>;
export function transformLength<T extends ObjectWithLengthInMm>({
  lengthInMm,
  ...rest
}: T): Omit<T, "lengthInMm"> & { length: Length } {
  return {
    ...rest,
    length: new Length(lengthInMm),
  };
}

type ObjectWithCoordinates = { coordinatesFromSourceToTarget: Point[] } & Record<string, unknown>;
export function transformCoordinates<T extends ObjectWithCoordinates>({
  coordinatesFromSourceToTarget,
  ...rest
}: T): Omit<T, "coordinatesFromSourceToTarget"> & { coordinates: DirectionalList<Point> } {
  return {
    ...rest,
    coordinates: new DirectionalList<Point>(coordinatesFromSourceToTarget),
  };
}
